/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Password } from 'primereact/password';
import { Dropdown } from 'primereact/dropdown';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { partnerConnectionsService } from '../../services/partner-connections.service';
import { partnersService } from '../../services/partners.service';
import { connectionTypesService } from '../../services/connection-types.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import type { Partner, ConnectionType } from '../../types/partners';
import { useValueSetOptions } from '../../hooks/useValueSetOptions';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { extractErrorMessage } from '../../utils/error-handler';
import ScreenHeader from '../../components/common/screen-header';

const LIST = '/partners?tab=connections';

const AUTH_TYPE_OPTIONS = [
  { label: 'Bearer / API token', value: 'BEARER' },
  { label: 'Basic', value: 'BASIC' },
  { label: 'OAuth2', value: 'OAUTH2' },
  { label: 'None', value: 'NONE' },
];

const EMPTY = {
  partnerId: '', name: '', connectionType: '', authType: '', connectionConfig: '',
  connectionLoginUser: '', connectionPassword: '', connectionAuthToken: '',
  sessionTimeoutPeriod: null as number | null, comments: '', isActive: true,
  // Values for the selected type's declared config fields, keyed by schema key.
  schemaValues: {} as Record<string, string>,
};

/**
 * One declared field on a connection type's `credentialSchema.config`.
 *
 * Every type already carries these — labels, hints, required flags — and the form
 * showed a raw JSON box regardless, so the operator had to know `instanceId` vs
 * `instance_id` by memory. A typo produced config the engine silently ignores,
 * which is the worst kind of failure: the save succeeds and nothing works.
 */
interface SchemaField {
  key: string;
  label?: string;
  hint?: string;
  required?: boolean;
  kind?: string;
}

/** The config fields the selected type declares, or [] when it declares none. */
function configFieldsOf(type?: ConnectionType | null): SchemaField[] {
  const raw = (type?.credentialSchema as { config?: unknown } | null)?.config;
  return Array.isArray(raw) ? (raw as SchemaField[]).filter((f) => f && f.key) : [];
}

/**
 * Create / edit a partner connection.
 *
 * A page rather than a dialog, like every other master in this app. The form
 * carries a partner, a type, credentials, the type's declared settings and a
 * JSON escape hatch — more than a modal shows without scrolling itself, and a
 * create worth deep-linking to and coming back from.
 */
const PartnerConnectionForm = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PARTNER_CONNECTIONS);
  const canUpdate = useCanUpdate(JOB_NAMES.PARTNER_CONNECTIONS);

  const [form, setForm] = useState({ ...EMPTY });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setField = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const [partners, setPartners] = useState<Partner[]>([]);
  const [types, setTypes] = useState<ConnectionType[]>([]);

  // Project the connection (and its partner + secret) live in. A connection must
  // sit in the SAME project as the AI model that binds it, so the project is
  // chosen here rather than left to the token — the backend honours ?app_id=.
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  const [appId, setAppId] = useState<string>('');
  const canManageProjects = accessibleProjects.length > 1;
  useEffect(() => {
    projectsService.getAccessible()
      .then((ps) => { setAccessibleProjects(ps); if (ps.length) setAppId((cur) => cur || ps[0]._id); })
      .catch(() => setAccessibleProjects([]));
  }, []);

  // Connection types are global; load once.
  useEffect(() => {
    connectionTypesService.getAllForDropdown().then(setTypes).catch(() => setTypes([]));
  }, []);

  // Partners are project-scoped, so reload them whenever the chosen project changes.
  useEffect(() => {
    if (isEdit) { partnersService.getAllForDropdown().then(setPartners).catch(() => setPartners([])); return; }
    partnersService.getAll({ page: 1, page_size: 500, include_deleted: false, app_id: appId || undefined } as any)
      .then((res) => setPartners(res.data || []))
      .catch(() => setPartners([]));
  }, [appId, isEdit]);

  const projectOptions = useMemo(() => accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id })), [accessibleProjects]);
  const partnerOptions = useMemo(() => partners.map((p) => ({ label: p.name || p._id, value: p._id })), [partners]);
  const typeOptions = useMemo(() => types.map((t) => ({ label: `${t.code} — ${t.name}`, value: t._id })), [types]);
  const authTypeOptions = useValueSetOptions('connectionAuthType', AUTH_TYPE_OPTIONS);

  const selectedType = types.find((t) => t._id === form.connectionType) || null;
  const schemaFields = configFieldsOf(selectedType);
  const typeCode = (tid?: string) => types.find((t) => t._id === tid)?.code || tid || '-';

  const { data: existing, isLoading } = useQuery({
    queryKey: ['partner-connection', id],
    queryFn: () => partnerConnectionsService.getById(id!),
    enabled: isEdit && !!id,
  });
  useBreadcrumbTitle(isEdit ? existing?.name : null);

  // Depends on `types` as well as the record: the declared fields cannot be split
  // out of connectionConfig until the type that declares them has loaded.
  useEffect(() => {
    if (!existing || !isEdit) return;
    const cfg = (existing.connectionConfig || {}) as Record<string, unknown>;
    setForm({
      partnerId: existing.partnerId || '',
      name: existing.name || '',
      connectionType: existing.connectionType || '',
      authType: existing.authType || '',
      connectionConfig: existing.connectionConfig ? JSON.stringify(existing.connectionConfig, null, 2) : '',
      connectionLoginUser: existing.connectionLoginUser || '',
      connectionPassword: '', connectionAuthToken: '',
      sessionTimeoutPeriod: existing.sessionTimeoutPeriod ?? null,
      comments: existing.comments || '',
      isActive: existing.isActive ?? true,
      // Declared fields go to their named inputs; anything else stays in the JSON
      // box so it is visible and survives the save.
      schemaValues: Object.fromEntries(
        configFieldsOf(types.find((tp) => tp._id === existing.connectionType)).map(
          (f) => [f.key, cfg[f.key] === undefined || cfg[f.key] === null ? '' : String(cfg[f.key])],
        ),
      ),
    });
  }, [existing, isEdit, types]);

  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'error' ? 'Error' : 'Success', detail, life: severity === 'error' ? 8000 : 3000 });

  /* Which credential inputs to show. A type that DECLARES its credentials decides
   * this, not the auth dropdown: picking the wrong auth type otherwise leaves no
   * input for a credential the type requires, and the save fails on a field never
   * shown.
   *
   * The auth type still governs types that declare nothing, which is every
   * hand-configured REST connection. */
  const declaredCredentials = ((selectedType?.credentialSchema as
    { credentials?: { key?: string }[] } | null)?.credentials ?? [])
    .map((c) => c?.key).filter(Boolean) as string[];

  const showBasic = declaredCredentials.length
    ? declaredCredentials.includes('connectionLoginUser')
      || declaredCredentials.includes('connectionPassword')
    : form.authType === 'BASIC';
  const showToken = declaredCredentials.length
    ? declaredCredentials.includes('connectionAuthToken')
    : form.authType === 'BEARER';

  const mutation = useMutation({
    mutationFn: async () => {
      const e: Record<string, string> = {};
      if (!form.name.trim()) e.name = 'Name is required';
      if (!form.authType) e.authType = 'Auth type is required';
      if (!isEdit) {
        if (!form.partnerId) e.partnerId = 'Partner is required';
        if (!form.connectionType) e.connectionType = 'Connection type is required';
      }
      let connectionConfig: any = undefined;
      if (form.connectionConfig.trim()) {
        try { connectionConfig = JSON.parse(form.connectionConfig); }
        catch { e.connectionConfig = 'Must be valid JSON'; }
      }

      // Named fields are layered OVER whatever the JSON box holds, never instead of
      // it. The box carries keys the schema does not describe, and rebuilding from
      // the named fields alone would delete them on the next save of an unrelated
      // field.
      if (schemaFields.length) {
        const base = (connectionConfig && typeof connectionConfig === 'object')
          ? { ...connectionConfig } : {};
        for (const f of schemaFields) {
          const v = (form.schemaValues?.[f.key] ?? '').trim();
          if (!v) {
            delete base[f.key];
            if (f.required) e[`schema.${f.key}`] = `${f.label || f.key} is required`;
          } else {
            base[f.key] = v;
          }
        }
        connectionConfig = base;
      }

      setErrors(e);
      if (Object.keys(e).length) throw new Error('validation');

      if (isEdit && id) {
        const payload: any = {
          name: form.name.trim(), authType: form.authType, connectionConfig,
          connectionLoginUser: form.connectionLoginUser,
          sessionTimeoutPeriod: form.sessionTimeoutPeriod ?? undefined,
          comments: form.comments, isActive: form.isActive,
        };
        if (form.connectionPassword) payload.connectionPassword = form.connectionPassword;
        if (form.connectionAuthToken) payload.connectionAuthToken = form.connectionAuthToken;
        await partnerConnectionsService.update(id, payload);
      } else {
        const payload: any = {
          partnerId: form.partnerId, name: form.name.trim(),
          connectionType: form.connectionType, authType: form.authType,
          connectionConfig, connectionLoginUser: form.connectionLoginUser || undefined,
          sessionTimeoutPeriod: form.sessionTimeoutPeriod ?? undefined,
          comments: form.comments || undefined,
        };
        if (form.connectionPassword) payload.connectionPassword = form.connectionPassword;
        if (form.connectionAuthToken) payload.connectionAuthToken = form.connectionAuthToken;
        await partnerConnectionsService.create(payload, appId || undefined);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-connections'] });
      notify('success', `Connection ${isEdit ? 'updated' : 'created'} successfully.`);
      setTimeout(() => navigate(LIST), 700);
    },
    onError: (err: any) => {
      if (err?.message === 'validation') return; // field errors already shown
      notify('error', extractErrorMessage(err, `Failed to ${isEdit ? 'update' : 'create'} connection`));
    },
  });

  const saving = mutation.isPending;
  const canSave = isEdit ? canUpdate : canCreate;

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <PermissionGuard jobName={JOB_NAMES.PARTNER_CONNECTIONS}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-PartnerConnection-Form">
        <ScreenHeader icon="pi pi-link" title={isEdit ? 'Edit Connection' : 'Create Connection'} subtitle="How this platform reaches a partner system" onBack={() => navigate(LIST)} />

        <form onSubmit={(ev) => { ev.preventDefault(); mutation.mutate(); }}>
          <div className="ds-card p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isEdit && canManageProjects && (
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="text-sm font-medium text-ink">Project *</label>
                  <Dropdown value={appId} onChange={(e) => { setAppId(e.value); setField('partnerId', ''); setErrors((x) => ({ ...x, partnerId: '' })); }}
                    options={projectOptions} optionLabel="label" optionValue="value" placeholder="Select project" filter />
                  <small className="text-gray-500">The connection, its partner and its secret all live in this project. Must match the model that binds it.</small>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Partner *</label>
                <Dropdown value={form.partnerId} onChange={(e) => { setField('partnerId', e.value); setErrors((x) => ({ ...x, partnerId: '' })); }}
                  options={partnerOptions} optionLabel="label" optionValue="value" placeholder="Select partner"
                  disabled={isEdit} filter className={errors.partnerId ? 'p-invalid' : ''} emptyMessage="No partners in this project" />
                {errors.partnerId && <small className="text-red-600">{errors.partnerId}</small>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Connection Type *</label>
                <Dropdown value={form.connectionType} onChange={(e) => { setField('connectionType', e.value); setErrors((x) => ({ ...x, connectionType: '' })); }}
                  options={typeOptions} optionLabel="label" optionValue="value" placeholder="Select type"
                  disabled={isEdit} className={errors.connectionType ? 'p-invalid' : ''} />
                {errors.connectionType && <small className="text-red-600">{errors.connectionType}</small>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Name *</label>
                <InputText value={form.name} onChange={(e) => { setField('name', e.target.value); setErrors((x) => ({ ...x, name: '' })); }}
                  className={errors.name ? 'p-invalid' : ''} />
                {errors.name && <small className="text-red-600">{errors.name}</small>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Auth Type *</label>
                <Dropdown value={form.authType} onChange={(e) => { setField('authType', e.value); setErrors((x) => ({ ...x, authType: '' })); }}
                  options={authTypeOptions} optionLabel="label" optionValue="value" placeholder="Select"
                  className={errors.authType ? 'p-invalid' : ''} />
                {errors.authType && <small className="text-red-600">{errors.authType}</small>}
              </div>

              {showBasic && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-ink">Login User</label>
                    <InputText value={form.connectionLoginUser} onChange={(e) => setField('connectionLoginUser', e.target.value)} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-ink">Password {isEdit ? '(leave blank to keep)' : ''}</label>
                    <Password value={form.connectionPassword} onChange={(e) => setField('connectionPassword', e.target.value)}
                      feedback={false} toggleMask inputClassName="w-full" placeholder={existing?.hasPassword ? '••••••••' : ''} />
                  </div>
                </>
              )}
              {showToken && (
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="text-sm font-medium text-ink">Auth Token {isEdit ? '(leave blank to keep)' : ''}</label>
                  <Password value={form.connectionAuthToken} onChange={(e) => setField('connectionAuthToken', e.target.value)}
                    feedback={false} toggleMask inputClassName="w-full" placeholder={existing?.hasAuthToken ? '••••••••' : ''} />
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Session Timeout (ms)</label>
                <InputNumber value={form.sessionTimeoutPeriod} onValueChange={(e) => setField('sessionTimeoutPeriod', e.value ?? null)} useGrouping={false} />
              </div>
              {isEdit && (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-ink">Active</label>
                  <div className="pt-1"><InputSwitch checked={form.isActive} onChange={(e: any) => setField('isActive', e.value)} /></div>
                </div>
              )}

              {/* The type's declared fields, as named inputs. Every connection type
                  carries these with labels and hints; showing a JSON box instead
                  meant the operator had to remember `instanceId` exactly, and a
                  near-miss saved fine and then silently did nothing. */}
              {schemaFields.length > 0 && (
                <div className="md:col-span-2">
                  <p className="mb-2 text-sm font-semibold text-ink">
                    {typeCode(form.connectionType)} settings
                  </p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {schemaFields.map((f) => (
                      <div key={f.key} className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-ink" htmlFor={`schema-${f.key}`}>
                          {f.label || f.key}
                          {f.required && <span className="ml-1 text-red-600">*</span>}
                        </label>
                        <InputText
                          id={`schema-${f.key}`}
                          value={form.schemaValues?.[f.key] ?? ''}
                          onChange={(ev) => setField('schemaValues', {
                            ...(form.schemaValues || {}), [f.key]: ev.target.value,
                          })}
                          className={errors[`schema.${f.key}`] ? 'p-invalid' : ''}
                          placeholder={f.hint || ''}
                        />
                        {f.hint && <small className="text-gray-500">{f.hint}</small>}
                        {errors[`schema.${f.key}`] && (
                          <small className="text-red-600">{errors[`schema.${f.key}`]}</small>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="text-sm font-medium text-ink">
                  {schemaFields.length ? 'Other configuration (advanced, JSON)' : 'Connection Config (JSON)'}
                </label>
                {schemaFields.length > 0 && (
                  <small className="text-gray-500">
                    Keys this type does not declare. Left as-is on save — the named
                    fields above are layered over it, never instead of it.
                  </small>
                )}
                <InputTextarea value={form.connectionConfig}
                  onChange={(e) => { setField('connectionConfig', e.target.value); setErrors((x) => ({ ...x, connectionConfig: '' })); }}
                  rows={6} className={`font-mono text-xs ${errors.connectionConfig ? 'p-invalid' : ''}`}
                  placeholder='{ "baseUrl": "https://api.partner.com", "healthPath": "/health" }' />
                {errors.connectionConfig && <small className="text-red-600">{errors.connectionConfig}</small>}
              </div>

              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="text-sm font-medium text-ink">Comments</label>
                <InputText value={form.comments} onChange={(e) => setField('comments', e.target.value)} />
              </div>
            </div>

            <p className="mt-3 text-xs text-ink-muted">
              <i className="pi pi-lock mr-1" /> Secrets are stored securely and never shown again — leave blank on edit to keep the existing value.
            </p>

            <div className="mt-4 px-4 py-3 -mx-4 -mb-4 bg-canvas dark:bg-gray-900/50 border-t border-line flex justify-end gap-2 rounded-b-lg">
              <Button label="Cancel" severity="secondary" outlined size="small" type="button" onClick={() => navigate(LIST)} disabled={saving} />
              {canSave && (
                <Button type="submit" label={saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={saving} size="small"
                  testId="PartnerConnectionForm.Button.Save" />
              )}
            </div>
          </div>
        </form>
      </div>
    </PermissionGuard>
  );
};

export default PartnerConnectionForm;
